import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  TextField,
  DialogContentText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { alpha } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import MergeRoundedIcon from "@mui/icons-material/MergeRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import dayjs from "dayjs";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import Overlay from "components/Overlay/Overlay";

const RequestorMergeOverlay = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const {
    overlayData,
    setOverlayName,
    triggerPageRefresh,
    showSnackbar,
    openDialog,
    closeDialog,
  } = useStateContext();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const requestors = useMemo(() => {
    return Array.isArray(overlayData?.Requestors) ? overlayData.Requestors : [];
  }, [overlayData?.Requestors]);

  const requestorById = useMemo(() => {
    return new Map(requestors.map((requestor) => [Number(requestor.id), requestor]));
  }, [requestors]);

  const selectedId = Number(overlayData?.SelectedRequestor?.id);

  const normalizeName = useCallback((value) => {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const damerauLevenshteinDistance = useCallback((a, b) => {
    const left = String(a || "");
    const right = String(b || "");

    const leftLength = left.length;
    const rightLength = right.length;

    if (leftLength === 0) return rightLength;
    if (rightLength === 0) return leftLength;

    const matrix = Array.from({ length: leftLength + 1 }, () =>
      new Array(rightLength + 1).fill(0),
    );

    for (let i = 0; i <= leftLength; i += 1) {
      matrix[i][0] = i;
    }

    for (let j = 0; j <= rightLength; j += 1) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= leftLength; i += 1) {
      for (let j = 1; j <= rightLength; j += 1) {
        const substitutionCost = left[i - 1] === right[j - 1] ? 0 : 1;

        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + substitutionCost,
        );

        if (
          i > 1 &&
          j > 1 &&
          left[i - 1] === right[j - 2] &&
          left[i - 2] === right[j - 1]
        ) {
          matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + 1);
        }
      }
    }

    return matrix[leftLength][rightLength];
  }, []);

  const normalizedSimilarity = useCallback((left, right) => {
    const a = String(left || "");
    const b = String(right || "");
    const maxLength = Math.max(a.length, b.length);

    if (!maxLength) return 1;

    const distance = damerauLevenshteinDistance(a, b);
    return Math.max(0, 1 - distance / maxLength);
  }, [damerauLevenshteinDistance]);

  const getNameMatchPercent = useCallback((baseName, candidateName) => {
    const base = normalizeName(baseName);
    const candidate = normalizeName(candidateName);

    if (!base || !candidate) return 0;
    if (base === candidate) return 100;

    const compactBase = base.replace(/\s+/g, "");
    const compactCandidate = candidate.replace(/\s+/g, "");
    const fullNameScore = normalizedSimilarity(compactBase, compactCandidate);

    const baseTokens = base.split(" ").filter(Boolean);
    const candidateTokens = candidate.split(" ").filter(Boolean);

    const tokenLengthsTotal = baseTokens.reduce(
      (sum, token) => sum + token.length,
      0,
    );

    const tokenScore =
      tokenLengthsTotal > 0
        ? baseTokens.reduce((sum, token) => {
            const bestTokenSimilarity = candidateTokens.reduce(
              (best, otherToken) =>
                Math.max(best, normalizedSimilarity(token, otherToken)),
              0,
            );
            return sum + bestTokenSimilarity * token.length;
          }, 0) / tokenLengthsTotal
        : 0;

    const firstNameScore = normalizedSimilarity(
      baseTokens[0] || "",
      candidateTokens[0] || "",
    );
    const lastNameScore = normalizedSimilarity(
      baseTokens[baseTokens.length - 1] || "",
      candidateTokens[candidateTokens.length - 1] || "",
    );

    const finalScore =
      fullNameScore * 0.55 +
      tokenScore * 0.2 +
      firstNameScore * 0.1 +
      lastNameScore * 0.15;

    return Math.max(0, Math.min(100, Math.round(finalScore * 100)));
  }, [normalizeName, normalizedSimilarity]);

  const initialMergeIds = useMemo(() => {
    if (!selectedId || !requestorById.has(selectedId)) {
      return requestors.length ? [Number(requestors[0].id)] : [];
    }
    return [selectedId];
  }, [requestorById, requestors, selectedId]);

  const initialOtherIds = useMemo(() => {
    const mergeSet = new Set(initialMergeIds);
    const keepRequestorName = requestorById.get(Number(initialMergeIds[0]))?.Name || "";

    return requestors
      .map((requestor) => Number(requestor.id))
      .filter((id) => !mergeSet.has(id))
      .map((id, index) => ({
        id,
        index,
        score: getNameMatchPercent(keepRequestorName, requestorById.get(id)?.Name),
      }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.index - b.index;
      })
      .map((item) => item.id);
  }, [getNameMatchPercent, initialMergeIds, requestorById, requestors]);

  const [requestorsToMergeIds, setRequestorsToMergeIds] = useState(initialMergeIds);
  const [otherRequestorIds, setOtherRequestorIds] = useState(initialOtherIds);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [otherSearch, setOtherSearch] = useState("");

  const filteredOtherRequestorIds = useMemo(() => {
    const query = String(otherSearch || "").trim().toLowerCase();
    if (!query) return otherRequestorIds;

    return otherRequestorIds.filter((requestorId) => {
      const requestor = requestorById.get(Number(requestorId));
      if (!requestor) return false;

      const name = String(requestor.Name || "").toLowerCase();
      return name.includes(query);
    });
  }, [otherSearch, otherRequestorIds, requestorById]);

  const keptRequestorName = useMemo(() => {
    const keepId = Number(requestorsToMergeIds[0]);
    return requestorById.get(keepId)?.Name || "";
  }, [requestorById, requestorsToMergeIds]);

  // Lightweight client-side preview of what will move to the kept requestor
  // (counts from the already-loaded list rows — no server round-trip).
  const mergePreview = useMemo(() => {
    if (requestorsToMergeIds.length < 2) return null;
    const keepId = Number(requestorsToMergeIds[0]);
    const keep = requestorById.get(keepId);
    const mergeRequestors = requestorsToMergeIds
      .slice(1)
      .map((id) => requestorById.get(Number(id)))
      .filter(Boolean);

    let phones = 0;
    let emails = 0;
    let addresses = 0;
    mergeRequestors.forEach((r) => {
      phones += (r.PhoneNumbers?.length || 0) + (r.Phones?.length || 0);
      if (r.Email) emails += 1;
      if (r.Address) addresses += 1;
    });

    return {
      keepName: keep?.Name || "the first requestor",
      mergeCount: mergeRequestors.length,
      phones,
      emails,
      addresses,
    };
  }, [requestorsToMergeIds, requestorById]);

  const formatDate = (value) => {
    if (!value) return "N/A";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("M/D/YYYY") : "N/A";
  };

  const moveRequestorToMerge = (requestorId, targetIndex) => {
    const requestorIdNumber = Number(requestorId);
    if (!otherRequestorIds.includes(requestorIdNumber)) return;

    const nextOther = otherRequestorIds.filter((id) => id !== requestorIdNumber);
    const safeIndex =
      typeof targetIndex === "number"
        ? Math.max(0, Math.min(targetIndex, requestorsToMergeIds.length))
        : requestorsToMergeIds.length;

    const nextMerge = [...requestorsToMergeIds];
    nextMerge.splice(safeIndex, 0, requestorIdNumber);

    setRequestorsToMergeIds(nextMerge);
    setOtherRequestorIds(nextOther);
  };

  const sortOtherRequestorIdsByMatch = useCallback(
    (ids, mergeIds) => {
      const keepId = Number((mergeIds || [])[0]);
      const keepName = requestorById.get(keepId)?.Name || "";

      return [...ids]
        .map((id, index) => ({
          id,
          index,
          score: getNameMatchPercent(keepName, requestorById.get(Number(id))?.Name),
        }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.index - b.index;
        })
        .map((item) => item.id);
    },
    [getNameMatchPercent, requestorById],
  );

  const moveRequestorToOther = (requestorId, targetIndex) => {
    const requestorIdNumber = Number(requestorId);
    if (!requestorsToMergeIds.includes(requestorIdNumber)) return;

    const nextMerge = requestorsToMergeIds.filter((id) => id !== requestorIdNumber);
    const nextOther = [...otherRequestorIds];
    if (!nextOther.includes(requestorIdNumber)) {
      nextOther.push(requestorIdNumber);
    }

    const sortedOther = sortOtherRequestorIdsByMatch(nextOther, nextMerge);

    setRequestorsToMergeIds(nextMerge);
    setOtherRequestorIds(sortedOther);
  };

  const handleDragEnd = (result) => {
    setIsDragging(false);

    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const sourceList = source.droppableId === "merge-requestors" ? requestorsToMergeIds : otherRequestorIds;
    const destList = destination.droppableId === "merge-requestors" ? requestorsToMergeIds : otherRequestorIds;
    const [movedId] = sourceList.splice(source.index, 1);

    if (source.droppableId === destination.droppableId) {
      const newList = [...sourceList];
      newList.splice(destination.index, 0, movedId);
      if (source.droppableId === "merge-requestors") {
        setRequestorsToMergeIds(newList);
      } else {
        setOtherRequestorIds(newList);
      }
    } else {
      const newSourceList = sourceList.filter((_, i) => i !== source.index);
      const newDestList = [...destList];
      newDestList.splice(destination.index, 0, movedId);

      if (destination.droppableId === "merge-requestors") {
        setRequestorsToMergeIds(newDestList);
        const sortedOther = sortOtherRequestorIdsByMatch(newSourceList, newDestList);
        setOtherRequestorIds(sortedOther);
      } else {
        setOtherRequestorIds(newDestList);
        setRequestorsToMergeIds(newSourceList);
      }
    }
  };

  const mergeRequestors = async () => {
    if (requestorsToMergeIds.length < 2) {
      showSnackbar(
        "Select at least 2 requestors in 'Requestors to Merge'. The first one is kept.",
        theme,
        "error",
      );
      return;
    }

    const keepRequestorId = Number(requestorsToMergeIds[0]);
    const mergeRequestorIds = requestorsToMergeIds.slice(1).map(Number);

    try {
      setIsSaving(true);

      const response = await api.post("/clients/mergeRequestors", {
        data: {
          clientId: Number(overlayData?.ClientID),
          keepRequestorId,
          mergeRequestorIds,
        },
      });

      const movedRequestorLinksCount =
        Number(response?.data?.movedRequestorLinksCount) || 0;
      const movedInvoiceToLinksCount =
        Number(response?.data?.movedInvoiceToLinksCount) || 0;

      if (socket && overlayData?.ClientID) {
        socket.emit("entity:saved", {
          modelType: "Client",
          recordId: Number(overlayData.ClientID),
          changeType: "Requestor",
          changeAction: "Merged",
          user: {
            id: currentUser?.id,
            name: currentUser?.Name || "User",
            imageThumbnail: currentUser?.ImageThumbnail,
          },
        });
      }

      triggerPageRefresh(
        `Requestors merged successfully. Re-linked ${movedRequestorLinksCount} requestor field(s) and ${movedInvoiceToLinksCount} invoicer field(s).`,
        theme,
      );

      closeDialog();
      setOverlayName("");
    } catch (error) {
      const statusCode = error?.response?.status;
      const baseMessage =
        error?.response?.data?.error ||
        "An error occurred while merging requestors.";
      const message = statusCode
        ? `${baseMessage} (HTTP ${statusCode})`
        : baseMessage;
      closeDialog();
      showSnackbar(message, theme, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMergeClick = () => {
    if (requestorsToMergeIds.length < 2) {
      showSnackbar(
        "Select at least 2 requestors in 'Requestors to Merge'. The first one is kept.",
        theme,
        "error",
      );
      return;
    }

    const mergedCount = requestorsToMergeIds.length - 1;
    const keepName = keptRequestorName || "the first requestor";
    const preview = mergePreview;

    const movedParts = [];
    if (preview) {
      if (preview.phones > 0)
        movedParts.push(`${preview.phones} phone number(s)`);
      if (preview.emails > 0) movedParts.push(`${preview.emails} email(s)`);
      if (preview.addresses > 0)
        movedParts.push(`${preview.addresses} address(es)`);
    }

    openDialog({
      theme,
      title: "Merge Requestors?",
      message: `Merge ${mergedCount} requestor${mergedCount === 1 ? "" : "s"} into "${keepName}"? This cannot be undone.`,
      customContent: (
        <Box>
          <DialogContentText>
            {`Merge ${mergedCount} requestor${mergedCount === 1 ? "" : "s"} into "${keepName}"? This cannot be undone.`}
          </DialogContentText>
          {preview && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: "12px",
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.primary.border2}`,
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Merge preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {movedParts.length > 0
                  ? `Will move to "${preview.keepName}": ${movedParts.join(", ")}. `
                  : "No phones, emails, or addresses found on the merged requestor(s). "}
                Services, invoices, and group memberships are re-linked;
                duplicate phones are skipped.
              </Typography>
            </Box>
          )}
        </Box>
      ),
      primaryAction: "Merge",
      secondaryAction: "Cancel",
      primaryOnClick: mergeRequestors,
      secondaryOnClick: closeDialog,
    });
  };

  const renderRequestorCard = (requestorId, index, listType) => {
    const requestor = requestorById.get(Number(requestorId));
    if (!requestor) return null;

    const isMergeList = listType === "merge";
    const isPrimaryKept = isMergeList && index === 0;

    const backgroundColor = isPrimaryKept
      ? alpha(theme.palette.primary.main, 0.12)
      : isMergeList
      ? alpha(theme.palette.warning.main, 0.12)
      : theme.palette.background.default;

    const borderColor = isPrimaryKept
      ? theme.palette.primary.main
      : isMergeList
      ? theme.palette.warning.main
      : theme.palette.primary.border2;

    const chipLabel = isPrimaryKept ? "Keep" : isMergeList ? "Merge" : "Other";
    const nameMatchPercent = isMergeList
      ? null
      : getNameMatchPercent(keptRequestorName, requestor.Name);

    return (
      <Box
        key={`requestor-${requestor.id}`}
        sx={{
          mb: 1,
          p: 1.25,
          borderRadius: "12px",
          bgcolor: backgroundColor,
          border: `1px solid ${borderColor}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: theme.palette.text.secondary,
                cursor: "grab",
              }}
            >
              <DragIndicatorRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="body1" fontWeight={700}>
              {requestor.Name || "Unnamed Requestor"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                bgcolor: isPrimaryKept
                  ? theme.palette.primary.main
                  : isMergeList
                  ? theme.palette.warning.main
                  : theme.palette.background.alt,
                color:
                  isPrimaryKept || isMergeList
                    ? theme.palette.background.default
                    : theme.palette.text.primary,
                fontWeight: 700,
              }}
            />
            {isMergeList ? (
              <IconButton
                size="small"
                onClick={() => moveRequestorToOther(requestor.id)}
                aria-label="Remove from merge list"
              >
                <RemoveRoundedIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={() => moveRequestorToMerge(requestor.id)}
                aria-label="Add to merge list"
              >
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          Services: {Number(requestor.ServicesCount || 0)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          Last Service: {formatDate(requestor.LastServiceDate)}
        </Typography>
        {!isMergeList && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Name Match: {nameMatchPercent}%
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Overlay
      id="requestor-merge"
      open={true}
      onClose={() => setOverlayName("")}
      title="Merge Requestors"
      subtitle="Drag requestors between lists or use +/- buttons. In the top list, the first requestor is kept and the rest are merged into it."
      maxWidthPx={1600}
      height="90vh"
      contentBg="default"
      scrollable={false}
      footer={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "flex-end" }}>
          <Button
            color="primary"
            variant="outlined"
            size="large"
            endIcon={<DisabledByDefaultRoundedIcon />}
            onClick={() => setOverlayName("")}
            sx={{ borderRadius: "12px" }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="large"
            endIcon={<MergeRoundedIcon />}
            disabled={isSaving || requestorsToMergeIds.length === 0}
            onClick={handleMergeClick}
            sx={{ borderRadius: "12px" }}
          >
            Merge
          </Button>
        </Box>
      }
    >
        <Box
          sx={{
            display: "flex",
            p: "1rem 1.5rem",
            width: "100%",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <DragDropContext
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
          >
            <Grid
              container
              spacing={2}
              sx={{ width: "100%", minHeight: 0, height: "100%" }}
            >
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ minHeight: 0, height: { xs: "auto", md: "100%" }, display: "flex" }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: { xs: "auto", md: "100%" },
                    minHeight: 0,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.default,
                    p: 1.25,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    Requestors to Merge
                  </Typography>
                <Droppable droppableId="merge-requestors" type="REQUESTOR">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        height: { xs: "30vh", md: "100%" },
                        maxHeight: { xs: "30vh", md: "100%" },
                        overflowY: "auto",
                        borderRadius: "12px",
                        p: 0.5,
                        bgcolor:
                          snapshot.isDraggingOver || isDragging
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                      }}
                    >
                      {requestorsToMergeIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          Add at least two requestors here to merge.
                        </Typography>
                      ) : (
                        requestorsToMergeIds.map((requestorId, index) => (
                          <Draggable key={requestorId} draggableId={String(requestorId)} index={index}>
                            {(drag) => (
                              <Box
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                              >
                                {renderRequestorCard(requestorId, index, "merge")}
                              </Box>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
                </Box>
              </Grid>

              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ minHeight: 0, height: { xs: "auto", md: "100%" }, display: "flex" }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: { xs: "auto", md: "100%" },
                    minHeight: 0,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.default,
                    p: 1.25,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    Other Requestors
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search other requestors"
                    value={otherSearch}
                    onChange={(event) => setOtherSearch(event.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Droppable droppableId="other-requestors" type="REQUESTOR">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          height: { xs: "30vh", md: "100%" },
                          maxHeight: { xs: "30vh", md: "100%" },
                          overflowY: "auto",
                          borderRadius: "12px",
                          p: 0.5,
                          bgcolor:
                            snapshot.isDraggingOver || isDragging
                              ? alpha(theme.palette.warning.main, 0.1)
                              : "transparent",
                        }}
                      >
                        {otherRequestorIds.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            No requestors remaining.
                          </Typography>
                        ) : filteredOtherRequestorIds.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            No requestors match your search.
                          </Typography>
                        ) : (
                          filteredOtherRequestorIds.map((requestorId, index) => (
                            <Draggable key={requestorId} draggableId={String(requestorId)} index={index}>
                              {(drag) => (
                                <Box
                                  ref={drag.innerRef}
                                  {...drag.draggableProps}
                                  {...drag.dragHandleProps}
                                >
                                  {renderRequestorCard(requestorId, index, "other")}
                                </Box>
                              )}
                            </Draggable>
                          ))
                        )}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              </Grid>
            </Grid>
          </DragDropContext>
        </Box>
    </Overlay>
  );
};

export default RequestorMergeOverlay;
